import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

/** How close to the screen's side a popover may sit. */
const VIEWPORT_PADDING = 8;

type PopoverContentRenderer = (close: () => void) => React.ReactNode;

type PopoverProps = {
  trigger: React.ReactNode;
  // Allow either a node or a render function that receives a close callback
  content: React.ReactNode | PopoverContentRenderer;
  placement?: "top" | "bottom" | "left" | "right";
  offset?: number;
  className?: string;
  // When true: outside clicks won't close; trigger only opens (not toggles) and you must call close() explicitly
  persistent?: boolean;
  // Optional callback when popover closes
  onClose?: () => void;
  // Optional controlled open state (future-proof). If provided, internal state mirrors this.
  open?: boolean;
  // Fires on every open/close intent (trigger click, outside click). Combined
  // with `open` this makes the popover fully controllable from the parent.
  onOpenChange?: (open: boolean) => void;
};

export const Popover = ({
  trigger,
  content,
  placement = "bottom",
  offset = 8,
  className = "",
  persistent = false,
  onClose,
  open,
  onOpenChange,
}: PopoverProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open! : internalOpen;
  const [entered, setEntered] = useState(false); // for fade-in
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [positionStyle, setPositionStyle] = useState<{
    top: number;
    left: number;
    transform?: string;
  }>({ top: 0, left: 0, transform: undefined });

  const doClose = useCallback(() => {
    if (!isControlled) setInternalOpen(false);
    onOpenChange?.(false);
    onClose?.();
  }, [isControlled, onClose, onOpenChange]);

  const doOpen = useCallback(() => {
    if (!isControlled) setInternalOpen(true);
    onOpenChange?.(true);
  }, [isControlled, onOpenChange]);

  const togglePopover = useCallback(() => {
    if (persistent) {
      if (!isOpen) doOpen();
      return;
    }
    if (isOpen) {
      doClose();
    } else {
      doOpen();
    }
  }, [persistent, isOpen, doOpen, doClose]);

  // sticky popovers
  const getPosition = useCallback(() => {
    if (!triggerRef.current)
      return { top: 0, left: 0, transform: undefined as string | undefined };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    // Centred on the trigger, but never past the screen's edge: a trigger 20px from the
    // side put half the popover off it. Needs the popover's own width, which the first
    // layout pass does not have yet — it centres by transform then, and the frame that
    // follows the open puts it right before the fade-in shows it.
    const centredX = (centre: number, translateY: string) => {
      const width = contentRef.current?.offsetWidth ?? 0;
      if (!width) return { left: centre, transform: `translate(-50%, ${translateY})` };
      const min = scrollX + VIEWPORT_PADDING;
      const max = scrollX + window.innerWidth - width - VIEWPORT_PADDING;
      return {
        left: Math.max(min, Math.min(centre - width / 2, max)),
        transform: `translate(0, ${translateY})`,
      };
    };

    switch (placement) {
      case "top":
        return {
          top: triggerRect.top + scrollY - offset,
          ...centredX(triggerRect.left + scrollX + triggerRect.width / 2, "-100%"),
        };
      case "right":
        return {
          top: triggerRect.top + scrollY + triggerRect.height / 2,
          left: triggerRect.right + scrollX + offset,
          transform: "translate(0, -50%)",
        };
      case "left":
        return {
          top: triggerRect.top + scrollY + triggerRect.height / 2,
          left: triggerRect.left + scrollX - offset,
          transform: "translate(-100%, -50%)",
        };
      case "bottom":
      default:
        return {
          top: triggerRect.bottom + scrollY + offset,
          ...centredX(triggerRect.left + scrollX + triggerRect.width / 2, "0"),
        };
    }
  }, [placement, offset]);

  // Close popover when clicking outside
  useEffect(() => {
    if (persistent) return; // skip outside close in persistent mode
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        isOpen &&
        contentRef.current &&
        triggerRef.current &&
        !contentRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        doClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, persistent, doClose]);

  // Ensure fade-in: mount at opacity 0, then flip to 1 on next frame
  useLayoutEffect(() => {
    if (isOpen) {
      setEntered(false);
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
  }, [isOpen]);

  const updatePosition = useCallback(() => {
    const next = getPosition();
    setPositionStyle((prev) => {
      if (
        prev.top === next.top &&
        prev.left === next.left &&
        prev.transform === next.transform
      ) {
        return prev;
      }
      return next;
    });
  }, [getPosition]);

  // Continuous positioning while open to follow moving triggers (e.g. map pans)
  useEffect(() => {
    if (!isOpen) return;
    let frame = requestAnimationFrame(function tick() {
      updatePosition();
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, updatePosition]);

  // Ensure position is accurate whenever popover opens or layout shifts
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, placement, offset, updatePosition]);

  // Optional: reflow on scroll/resize to keep position correct
  useEffect(() => {
    if (!isOpen) return;
    const handle = () => setEntered((v) => v); // trigger re-render
    window.addEventListener("scroll", handle, { passive: true });
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={togglePopover}
        className="inline-block cursor-pointer"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        {trigger}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={contentRef}
            role="dialog"
            aria-modal="false"
            className={`absolute z-50 min-w-[200px] rounded-full border border-border bg-surface-card p-2 shadow-lg ${className}`}
            style={{
              ...positionStyle,
              position: "absolute",
              opacity: entered ? 1 : 0,
              transition: "opacity 150ms ease-out",
              willChange: "opacity",
            }}
          >
            {typeof content === "function"
              ? (content as PopoverContentRenderer)(doClose)
              : content}
          </div>,
          document.body,
        )}
    </>
  );
};
